import React, { useState, useCallback, useEffect } from 'react';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import type { UserItem } from '../../types';

function statusBadge(status: string): { label: string; className: string } {
  switch (status) {
    case 'CONFIRMED':
      return { label: '有効', className: 'bg-green-100 text-green-700' };
    case 'FORCE_CHANGE_PASSWORD':
      return { label: 'パスワード変更待ち', className: 'bg-yellow-100 text-yellow-700' };
    default:
      return { label: status, className: 'bg-slate-100 text-slate-600' };
  }
}

export const UserListPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiService.getUsers();
      setUsers(result.users);
    } catch {
      setError('ユーザー一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setIsInviting(true);
    try {
      await apiService.createUser(inviteEmail.trim());
      setShowInviteDialog(false);
      setInviteEmail('');
      fetchUsers();
    } catch {
      alert('ユーザーの招待に失敗しました');
    } finally {
      setIsInviting(false);
    }
  };

  const handleDelete = async (targetEmail: string) => {
    if (!window.confirm(`「${targetEmail}」を削除しますか？この操作は取り消せません。`)) return;
    try {
      await apiService.deleteUser(targetEmail);
      fetchUsers();
    } catch {
      alert('ユーザーの削除に失敗しました');
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">ユーザー管理</h1>
        <button
          onClick={() => setShowInviteDialog(true)}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          + ユーザーを招待
        </button>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-slate-200 bg-slate-50">
              <th className="px-5 py-3 text-left font-semibold text-slate-500">メールアドレス</th>
              <th
                className="px-5 py-3 text-left font-semibold text-slate-500"
                style={{ width: 180 }}
              >
                ステータス
              </th>
              <th
                className="px-5 py-3 text-left font-semibold text-slate-500"
                style={{ width: 180 }}
              >
                作成日
              </th>
              <th
                className="px-5 py-3 text-right font-semibold text-slate-500"
                style={{ width: 80 }}
              >
                操作
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4} className="px-5 py-8 text-center text-slate-400">
                  読み込み中...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-5 py-8 text-center text-slate-400">
                  ユーザーがいません
                </td>
              </tr>
            ) : (
              users.map((u, idx) => {
                const badge = statusBadge(u.status);
                const isSelf = currentUser?.email === u.email;
                return (
                  <tr
                    key={u.email}
                    className={`border-b border-slate-100 ${idx % 2 === 1 ? 'bg-slate-50/50' : ''}`}
                  >
                    <td className="px-5 py-3">{u.email}</td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-500">
                      {new Date(u.createdAt).toLocaleDateString('ja-JP')}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {!isSelf && (
                        <button
                          onClick={() => handleDelete(u.email)}
                          className="text-red-500 hover:text-red-700"
                        >
                          削除
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showInviteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-bold text-slate-900">ユーザーを招待</h2>
            <form onSubmit={handleInvite}>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                メールアドレス
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="user@example.com"
                required
                className="mb-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteDialog(false);
                    setInviteEmail('');
                  }}
                  className="rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-100"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={isInviting}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {isInviting ? '送信中...' : '招待を送信'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
